import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
  Spinner,
} from "reactstrap";

import classnames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { addMedia, deleteMedia, getMedia } from "../../slices/0-media/thunk";
import MediaImage from "./MediaImage";
import Pagination from "../../Components/Common/Pagination";

const MediaLibraryModal = ({
  isOpen,
  onClose,
  onSelect,
  multi = false,
  showTabs = { images: true, videos: false, documents: false },
}) => {
  const dispatch = useDispatch();

  const { images, videos, documents, loading, uploading, pagination } =
    useSelector((state) => state.Media);

  const [activeTab, setActiveTab] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 12;

  const files = { images, videos, documents };

  useEffect(() => {
    if (showTabs.images) {
      setActiveTab("images");
    } else if (showTabs.videos) {
      setActiveTab("videos");
    } else if (showTabs.documents) {
      setActiveTab("documents");
    }
  }, [showTabs]);

  // Fetch media when modal opens or tab/page changes
  useEffect(() => {
    if (isOpen && activeTab) {
      // console.log("🎯 Before dispatch - pagination state:", pagination);

      dispatch(
        getMedia({
          type: activeTab,
          page: currentPage,
          limit: itemsPerPage,
        }),
      ).then(() => {
        // console.log("🎯 After dispatch - pagination state:", pagination);
      });

      setSelectedItems([]);
    }
  }, [isOpen, activeTab, currentPage]);

  // Reset to page 1 when switching tabs
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const handleSelect = (item) => {
    if (multi) {
      if (selectedItems.some((s) => s.url === item.url)) {
        setSelectedItems(selectedItems.filter((s) => s.url !== item.url));
      } else {
        setSelectedItems([...selectedItems, item]);
      }
    } else {
      onSelect(item.url);
      onClose();
    }
  };

  const handleSave = () => {
    onSelect(selectedItems.map((s) => s.url));
    onClose();
  };

  const handleDelete = (id) => {
    if (window.confirm("هل انت متأكد من مسح الملف")) {
      dispatch(deleteMedia({ type: activeTab, id })).then(() => {
        // dispatch(
        //   getMedia({
        //     type: activeTab,
        //     page: currentPage,
        //     limit: itemsPerPage,
        //   })
        // );
      });
      setSelectedItems((prev) => prev.filter((s) => s.id !== id));
    }
  };

  const isSelected = (url) => selectedItems.some((s) => s.url === url);

  const handleUpload = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;

    if (activeTab === "images") input.accept = "image/*";
    if (activeTab === "videos") input.accept = "video/*";
    if (activeTab === "documents") input.accept = ".pdf";

    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      if (!files.length) return;

      try {
        for (const file of files) {
          const title = file.name;

          await dispatch(
            addMedia({
              type: activeTab,
              file,
              title,
            }),
          ).unwrap();
        }

        setCurrentPage(1);

        dispatch(
          getMedia({
            type: activeTab,
            page: 1,
            limit: itemsPerPage,
          }),
        );
      } catch (error) {
        console.error("Upload failed:", error);
      }
    };

    input.click();
  };

  const handlePageChange = (page) => {
    const currentPagination = pagination?.[activeTab];
    if (
      currentPagination &&
      page >= 1 &&
      page <= currentPagination.totalPages
    ) {
      setCurrentPage(page);
    }
  };

  const currentPagination = pagination?.[activeTab] || {};

  // console.log("🔍 Component render - activeTab:", activeTab);
  // console.log("🔍 Component render - pagination:", pagination);
  // console.log("🔍 Component render - currentPagination:", currentPagination);

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
    gap: "15px",
    minHeight: "300px",
  };

  const renderDeleteIcon = (id) => (
    <button
      onClick={() => handleDelete(id)}
      style={{
        position: "absolute",
        zIndex: 99,
        top: 8,
        right: 8,
        background: "rgba(0,0,0,0.6)",
        border: "none",
        borderRadius: "50%",
        width: 28,
        height: 28,
        color: "#fff",
        fontSize: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      }}
      title="حذف"
    >
      🗑️
    </button>
  );

  const renderLoader = () => (
    <div
      className="d-flex justify-content-center align-items-center w-100 py-5"
      style={{ minHeight: "300px" }}
    >
      <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
    </div>
  );

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl">
      <ModalHeader toggle={onClose}>📂 مكتبة الوسائط</ModalHeader>
      <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Button color="primary" onClick={handleUpload} disabled={uploading}>
            {uploading ? (
              <Spinner size="sm" color="light" />
            ) : activeTab === "images" ? (
              "📸 رفع صورة"
            ) : activeTab === "videos" ? (
              "🎥 رفع فيديو"
            ) : (
              "📄 رفع ملف"
            )}
          </Button>
          <Nav tabs>
            {showTabs.images && (
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "images" })}
                  onClick={() => toggleTab("images")}
                >
                  الصور
                </NavLink>
              </NavItem>
            )}
            {showTabs.videos && (
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "videos" })}
                  onClick={() => toggleTab("videos")}
                >
                  الفيديوهات
                </NavLink>
              </NavItem>
            )}
            {showTabs.documents && (
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "documents" })}
                  onClick={() => toggleTab("documents")}
                >
                  المستندات
                </NavLink>
              </NavItem>
            )}
          </Nav>
        </div>

        {/*  pagination */}
        {currentPagination.total > 0 && (
          <div className="mb-2 text-muted">
            عرض {(currentPage - 1) * itemsPerPage + 1} -{" "}
            {Math.min(currentPage * itemsPerPage, currentPagination.total)} من
            أصل {currentPagination.total}
          </div>
        )}

        <TabContent activeTab={activeTab} className="mt-3">
          {/* Images */}
          <TabPane tabId="images">
            {loading ? (
              renderLoader()
            ) : (
              <div style={gridStyle}>
                {files.images?.map((img) => (
                  <MediaImage
                    key={img.id}
                    img={img}
                    isSelected={isSelected}
                    handleSelect={handleSelect}
                    renderDeleteIcon={renderDeleteIcon}
                  />
                ))}
              </div>
            )}
          </TabPane>

          {/* Videos */}
          <TabPane tabId="videos">
            {loading ? (
              renderLoader()
            ) : (
              <div style={gridStyle}>
                {files.videos?.map((vid) => (
                  <div
                    key={vid.id}
                    style={{
                      position: "relative",
                      width: "100%",
                      height: 150,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected(vid.url)}
                      onChange={() => handleSelect(vid)}
                      style={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        zIndex: 2,
                      }}
                    />
                    {renderDeleteIcon(vid.id)}
                    <video
                      src={vid.url}
                      onClick={() => window.open(vid.url, "_blank")}
                      style={{
                        width: "100%",
                        height: "100%",
                        border: isSelected(vid.url)
                          ? "3px solid #007bff"
                          : "1px solid #ddd",
                        borderRadius: 8,
                      }}
                      controls
                    />
                  </div>
                ))}
              </div>
            )}
          </TabPane>

          {/* Documents */}
          <TabPane tabId="documents">
            {loading ? (
              renderLoader()
            ) : (
              <ul className="list-group">
                {files.documents?.map((doc) => (
                  <li
                    key={doc.id}
                    className="list-group-item d-flex align-items-center justify-content-between"
                  >
                    <span
                      style={{ cursor: "pointer" }}
                      onClick={() => window.open(doc.url, "_blank")}
                    >
                      📄 {doc.originalName || doc.name}
                    </span>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="checkbox"
                        checked={isSelected(doc.url)}
                        onChange={() => handleSelect(doc)}
                      />
                      <button
                        onClick={() => handleDelete(doc.id)}
                        className="btn btn-sm btn-danger"
                      >
                        🗑️
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </TabPane>
        </TabContent>

        {/*  currentPagination */}
        {!loading && currentPagination.totalPages > 0 && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={currentPagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        {multi && (
          <div className="text-end mt-3">
            <Button color="success" onClick={handleSave}>
              💾 حفظ الاختيارات ({selectedItems.length})
            </Button>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default MediaLibraryModal;
